import React, { useEffect, useState, useCallback } from 'react';
import {
  DialogContent,
  Button,
  Stack,
  Typography,
  Alert,
  Box,
  IconButton,
  TextField,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import { ChevronLeft, Close as CloseIcon, Add } from '@mui/icons-material';
import { TransportMethod } from '@domain/shipment/models/TransportMethod';
import { PurchaseOrderItem } from '@domain/purchasing';
import { Shipment } from '@domain/shipment';
import { useWarehouses } from '@features/warehouses/hooks/useWarehouseOperations';
import { useShipmentMethods } from '@features/shipments/hooks/useShipmentMethods';
import {
  ProductDisplay,
  WarehouseSelection,
  LeadTimeTimeline
} from '@features/shipments/components/shared';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import CreateShipmentMethodDialog from '@features/shipments/components/methods/CreateShipmentMethodDialog';
import { ShipmentMethodPreview } from '@shared/ui/common/ShipmentMethodPreview';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { addDays } from 'date-fns';

interface CreateShipmentDetailsProps {
  onFinish: (shipment?: Shipment) => void;
  onBack?: () => void;
  onClose?: () => void;
  purchaseOrderItems: PurchaseOrderItem[];
}

export const CreateShipmentDetails: React.FC<CreateShipmentDetailsProps> = ({
  onFinish,
  onBack,
  onClose,
  purchaseOrderItems
}) => {

  const { warehouses } = useWarehouses();
  const { shipmentMethods, createShipmentMethod } = useShipmentMethods();

  const [createMethodDialogOpen, setCreateMethodDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    warehouseId: '',
    transportMethod: TransportMethod.BOAT,
    departureDate: new Date(),
    arrivalDate: new Date(),
    processedDate: new Date(),
    selectedShipmentMethodId: ''
  });

  // Helper functions
  const getLatestProductionReadyDate = useCallback(() => {
    return purchaseOrderItems.reduce((latest, item) => {
      const productionReadyDate = item.productionReadyDate || new Date();
      return productionReadyDate > latest ? productionReadyDate : latest;
    }, new Date(0));
  }, [purchaseOrderItems]);

  const getLongestProcessingDuration = useCallback(() => {
    return purchaseOrderItems.reduce((max, item) => {
      const processingDuration = item.product.processingDuration || 0;
      return processingDuration > max ? processingDuration : max;
    }, 0);
  }, [purchaseOrderItems]);

  const calculateDates = useCallback((shipmentDuration: number) => {
    const latestProductionReadyDate = getLatestProductionReadyDate();
    const longestProcessingDuration = getLongestProcessingDuration();

    const departureDate = latestProductionReadyDate;
    const arrivalDate = new Date(departureDate.getTime() + shipmentDuration * 24 * 60 * 60 * 1000);
    const processedDate = new Date(arrivalDate.getTime() + longestProcessingDuration * 24 * 60 * 60 * 1000);

    return { departureDate, arrivalDate, processedDate };
  }, [getLatestProductionReadyDate, getLongestProcessingDuration]);

  const calculateLeadTime = useCallback(() => {
    if (!purchaseOrderItems.length || !formData.selectedShipmentMethodId) return;

    const selectedMethod = shipmentMethods?.find(method => method.id === formData.selectedShipmentMethodId);
    if (!selectedMethod) return;

    const { departureDate, arrivalDate, processedDate } = calculateDates(selectedMethod.duration);

    setFormData(prev => ({
      ...prev,
      departureDate,
      arrivalDate,
      processedDate
    }));
  }, [purchaseOrderItems, formData.selectedShipmentMethodId, shipmentMethods, calculateDates]);

  useEffect(() => {
    if (formData.selectedShipmentMethodId && purchaseOrderItems.length > 0) {
      calculateLeadTime();
    }
  }, [formData.selectedShipmentMethodId, purchaseOrderItems, calculateLeadTime]);

  const handleShipmentMethodSelect = (method: ShipmentMethod) => {

    const departureDate = getLatestProductionReadyDate();
    const arrivalDate = addDays(departureDate, method.duration);
    const processedDate = addDays(arrivalDate, getLongestProcessingDuration());


    setFormData(prev => ({
      ...prev,
      selectedShipmentMethodId: method.id,
      transportMethod: method.transportMethod,
      departureDate: departureDate,
      arrivalDate: arrivalDate,
      processedDate: processedDate,
      duration: method.duration,
    }));
  };

  const handleCreateShipmentMethod = async (newMethod: any) => {
    try {
      const result = await createShipmentMethod({
        transportMethod: newMethod.transportMethod,
        duration: newMethod.duration,
        name: newMethod.name,
      });

      if (result) {
        setFormData(prev => ({ ...prev, selectedShipmentMethodId: result.id }));
        setCreateMethodDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to create shipment method:', error);
    }
  };

  const handleSubmit = () => {
    setError(null);

    const shipment: Shipment = {
      name: formData.name.trim(),
      transportMethod: formData.transportMethod,
      departureDate: formData.departureDate,
      arrivalDate: formData.arrivalDate,
      destinationWarehouse: warehouses?.find(w => w.id === formData.warehouseId),
      processedDate: formData.processedDate,
      purchaseOrderItems
    };

    onFinish(shipment);
  };

  const isFormValid = formData.warehouseId &&
    formData.transportMethod &&
    formData.departureDate &&
    formData.arrivalDate &&
    formData.processedDate;

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="flex-start" alignItems="center" width="100%">

          {onBack && (
            <IconButton onClick={onBack}>
              <ChevronLeft />
            </IconButton>
          )}

          <Typography variant="h6" flexGrow={1}>Create New Shipment</Typography>

          {onClose && (
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ p: 4, gap: 4, display: 'flex', flexDirection: 'column' }}>
        <Stack flex={1} gap={4}>

          <Box>
            <Typography variant="subtitle2" mb={2}>Shipment Method</Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {shipmentMethods?.map((method) => (
                <ShipmentMethodPreview
                  onClick={() => handleShipmentMethodSelect(method)}
                  method={method} selected={formData.selectedShipmentMethodId === method.id} />
              ))}

              <Button
                variant="text"
                startIcon={<Add />}
                onClick={() => setCreateMethodDialogOpen(true)}
                size="small"
                color="secondary"
                sx={{ p: 2 }}
              >
                Add new method
              </Button>
            </Stack>
          </Box>

          {formData.selectedShipmentMethodId && (
            <Stack gap={4}>
              <WarehouseSelection
                warehouses={warehouses || []}
                selectedWarehouseId={formData.warehouseId}
                onWarehouseChange={(warehouseId) => setFormData(prev => ({ ...prev, warehouseId }))}
              />

              {formData.warehouseId && (
                <>
                  <LeadTimeTimeline
                    departureDate={formData.departureDate}
                    arrivalDate={formData.arrivalDate}
                    processedDate={formData.processedDate}
                    onDepartureDateChange={(date) => setFormData(prev => ({ ...prev, departureDate: date }))}
                    onArrivalDateChange={(date) => setFormData(prev => ({ ...prev, arrivalDate: date }))}
                    onProcessedDateChange={(date) => setFormData(prev => ({ ...prev, processedDate: date }))}
                  />

                  <Stack spacing={1} justifyContent="flex-start" alignItems="flex-start">
                    <Typography variant="subtitle2" color="text.primary">
                      Shipment Name
                    </Typography>
                    <TextField
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                </>
              )}
            </Stack>
          )}

        </Stack>





        <Divider />

        <Stack sx={{ p: 0 }} gap={2}>
          <Typography variant="subtitle2" color="text.primary">Products to ship</Typography>
          <ProductDisplay purchaseOrderItems={purchaseOrderItems} />
        </Stack>

        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

      </DialogContent >

      {/* Bottom Action Bar */}
      < Box sx={{
        py: 3,
        px: 4,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }
      }>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Button
            variant="contained"
            disabled={!isFormValid}
            onClick={handleSubmit}
          >
            Create Shipment
          </Button>
        </Stack>
      </Box >

      <CreateShipmentMethodDialog
        open={createMethodDialogOpen}
        onClose={() => setCreateMethodDialogOpen(false)}
        onSuccess={handleCreateShipmentMethod}
      />

    </>
  );
};
